import { LineChart } from "@/components/charts/line-chart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBrandColor } from "@/lib/context/brand-color-context";
import {
  useCombinedInstrument,
  useCombinedInstrumentChartData,
  useCombinedInstrumentStatistics,
} from "@/lib/hooks/use-combined-instruments";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { InstrumentSyncAlert } from "@/pages/settings/portfolios/detail/products/instrument-sync-status";
import { T } from "./combined-instrument-chart-dialog.i18n";

type CombinedInstrumentChartDialogProps = {
  open: boolean;
  id: string | null;
  name: string | null;
  isin: string | null;
  onClose: () => void;
};

export function CombinedInstrumentChartDialog({
  open,
  id,
  name,
  isin,
  onClose,
}: CombinedInstrumentChartDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const brandColor = useBrandColor();

  const { data: chartData, isLoading: isLoadingChart } = useCombinedInstrumentChartData(
    open ? id : null,
  );
  const { data: stats, isLoading: isLoadingStats } = useCombinedInstrumentStatistics(
    open ? id : null,
  );
  // The health of the computed series (QL-333): why an empty chart is empty,
  // and whether the numbers shown come from a recompute that last succeeded.
  const { data: detail } = useCombinedInstrument(open ? id : null);
  const health = detail?.instrumentSync ?? null;

  const isLoading = isLoadingChart || isLoadingStats;
  const timeseries = chartData?.timeseries ?? [];
  const first = timeseries[0];
  const last = timeseries[timeseries.length - 1];

  const formatPct = (value: number) => (Number.isNaN(value) ? "N/A" : `${value.toFixed(2)}%`);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
          <DialogDescription>{isin}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
        ) : timeseries.length === 0 ? (
          <div className="space-y-4">
            <InstrumentSyncAlert health={health} lang={userLanguage} variant="recompute" />
            <div className="py-8 text-center text-muted-foreground">
              {health ? t.na : t.notComputed}
            </div>
          </div>
        ) : (
          <>
            <InstrumentSyncAlert health={health} lang={userLanguage} variant="recompute" />
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">{t.expectedReturn}: </span>
                <span className="font-medium">
                  {stats ? formatPct(stats.expectedReturn) : t.na}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">{t.expectedVolatility}: </span>
                <span className="font-medium">
                  {stats ? formatPct(stats.expectedVolatility) : t.na}
                </span>
              </div>
            </div>

            <LineChart
              data={timeseries}
              width="container"
              height={300}
              lang={userLanguage}
              primaryColor={brandColor ?? undefined}
              extraYGridlines={1}
            />

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              {first && (
                <span>
                  {t.firstDataPoint}: {first.date} ({first.value.toFixed(2)})
                </span>
              )}
              {last && (
                <span>
                  {t.lastDataPoint}: {last.date} ({last.value.toFixed(2)})
                </span>
              )}
              {chartData && (
                <span>
                  {t.currency}: {chartData.currencyIso}
                </span>
              )}
              <span>
                {t.dataPoints}: {timeseries.length}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
